import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { api, jsonBody, queryString } from "../../api/client";
import type { ListResponse } from "../../api/types";
import { useVenueScope } from "../../app/venueScope";
import { formatDate, formatDateTime, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Modal,
  Notice,
  PageHeader,
  StatusPill,
} from "../../ui/components";
import { SkeletonPageHeader, SkeletonTable } from "../../ui/skeletons";

type ReviewStatus = "under_review" | "accepted" | "declined" | "completed";

interface PortalAction {
  id: string;
  customerId: string;
  bookingId: string;
  bookingReference: string;
  bookingTitle: string;
  bookingTimezone: string;
  type: string;
  payload: Record<string, unknown>;
  status: string;
  reviewReason?: string;
  createdAt: string;
  updatedAt: string;
}

const actionTransitions: Record<string, ReviewStatus[]> = {
  submitted: ["under_review", "accepted", "declined"],
  under_review: ["accepted", "declined", "completed"],
  accepted: ["completed", "declined"],
};

function requestSummary(item: PortalAction): string {
  const message = typeof item.payload.message === "string" ? item.payload.message : "";
  if (item.type === "choice_response") {
    const key = typeof item.payload.choiceKey === "string" ? item.payload.choiceKey : "Choice";
    const value =
      typeof item.payload.choiceValue === "string"
        ? item.payload.choiceValue
        : "No response supplied";
    return `${titleCase(key)}: ${value}`;
  }
  if (item.type === "change_request") {
    const start =
      typeof item.payload.proposedStart === "string"
        ? formatDateTime(item.payload.proposedStart, item.bookingTimezone)
        : "not supplied";
    const end =
      typeof item.payload.proposedEnd === "string"
        ? formatDateTime(item.payload.proposedEnd, item.bookingTimezone)
        : "not supplied";
    return `${message} Proposed schedule: ${start} to ${end}.`.trim();
  }
  return message || "No message supplied.";
}

export function PortalActionsPage() {
  // The inbox answers to the venue in the top bar like every other page: only requests about
  // hires holding space there are shown, so somebody looking after one venue reads only theirs.
  const { selectedVenueId: venueId, loading: venueLoading } = useVenueScope();
  const listQuery = queryString({ venueId });
  const actions = useQuery({
    queryKey: ["portal", "staff-actions", venueId],
    queryFn: () => api<ListResponse<PortalAction>>(`/api/v1/portal/actions${listQuery}`),
    enabled: !venueLoading,
  });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [reviewTarget, setReviewTarget] = useState<{ item: PortalAction; status: ReviewStatus }>();
  const [reviewReason, setReviewReason] = useState("");

  function openReview(item: PortalAction, status: ReviewStatus) {
    setReviewTarget({ item, status });
    setReviewReason(
      status === "under_review"
        ? "Venue team has started reviewing this request."
        : "Decision recorded by venue team.",
    );
  }

  async function review(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!reviewTarget) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/portal/actions/${reviewTarget.item.id}/review`, {
        method: "PUT",
        body: jsonBody({ status: reviewTarget.status, reason: reviewReason }),
      });
      setReviewTarget(undefined);
      setReviewReason("");
      await actions.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (actions.isLoading)
    return (
      <>
        <SkeletonPageHeader />
        <SkeletonTable columns={6} rows={4} />
      </>
    );
  if (actions.error) return <ErrorPanel error={actions.error} />;
  const items = actions.data?.items ?? [];
  return (
    <>
      <PageHeader
        title="Customer requests"
        description="Review messages and requests sent by customers from their portal."
      />
      {error ? <ErrorPanel error={error} /> : null}
      <Notice tone="info">
        A request does not change the booking by itself. Review it here, then update the booking
        after checking dates and costs.
      </Notice>
      <Card>
        <CardHeader title="Customer requests" />
        {items.length ? (
          <DataTable
            caption="Customer portal requests"
            items={items}
            getKey={(item) => item.id}
            columns={[
              { label: "Received", render: (item) => formatDate(item.createdAt) },
              { label: "Type", render: (item) => titleCase(item.type) },
              {
                label: "Booking",
                render: (item) => (
                  <>
                    <strong>{item.bookingReference}</strong>
                    <br />
                    <small>{item.bookingTitle}</small>
                  </>
                ),
              },
              {
                label: "Request",
                render: (item) => <span className="wrap-text">{requestSummary(item)}</span>,
              },
              {
                label: "Status",
                render: (item) => (
                  <>
                    <StatusPill value={item.status} />
                    {item.reviewReason ? (
                      <>
                        <br />
                        <small>{item.reviewReason}</small>
                      </>
                    ) : null}
                  </>
                ),
              },
              {
                label: "Actions",
                render: (item) => (
                  <div className="inline">
                    <Link
                      className="button secondary small"
                      to={`/bookings/${encodeURIComponent(item.bookingId)}`}
                    >
                      Open booking
                    </Link>
                    {(actionTransitions[item.status] ?? []).map((status) => (
                      <button
                        className={`button ${status === "accepted" ? "primary" : "secondary"} small`}
                        type="button"
                        disabled={working}
                        key={status}
                        onClick={() => openReview(item, status)}
                      >
                        {status === "under_review" ? "Start review" : titleCase(status)}
                      </button>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        ) : (
          <EmptyState
            title="Inbox clear"
            description="New customer portal requests will appear here."
          />
        )}
      </Card>
      <Modal
        open={Boolean(reviewTarget)}
        onClose={() => setReviewTarget(undefined)}
        title={reviewTarget ? `${titleCase(reviewTarget.status)} request` : "Review request"}
        description={
          reviewTarget
            ? `${reviewTarget.item.bookingReference} · ${reviewTarget.item.bookingTitle}`
            : undefined
        }
      >
        {reviewTarget ? (
          <form className="form-stack" onSubmit={review}>
            <Notice tone="info">
              This records your response to the customer. Make any approved booking, date, or
              payment change from the booking itself.
            </Notice>
            <Field
              label="Response to customer"
              hint="The customer can see this message, and it is kept with the request."
            >
              <textarea
                value={reviewReason}
                onChange={(event) => setReviewReason(event.target.value)}
                required
                minLength={3}
                maxLength={2000}
              />
            </Field>
            <FormActions
              submitting={working}
              submitLabel={`Confirm ${titleCase(reviewTarget.status)}`}
              onCancel={() => setReviewTarget(undefined)}
            />
          </form>
        ) : null}
      </Modal>
    </>
  );
}
